/**
 * API client for photo management Worker
 */

import { api } from './api-client';

export interface Photo {
  id: number;
  country_code: string;
  photo_url: string;
  unsplash_id: string | null;
  photographer: string | null;
  photographer_url: string | null;
  sort_order: number;
  created_at: string;
}

export interface CountryPhotos {
  scenic?: Photo;
  city?: Photo;
}

export interface SeedingStatus {
  totalCountries: number;
  targetPhotosPerCountry: number;
  completedCountries: number;
  remainingCountries: number;
  rateLimitRemaining: number;
  rateLimitResetsAt: string;
  errors: Array<{ country_code: string; error: string }>;
}

export interface SeedingResult {
  processed: number;
  processedCountries: string[];
  errors: Array<{ code: string; error: string }>;
  completedCountries: number;
  remainingCountries: number;
  rateLimitRemaining: number;
  message: string;
}

export interface RateLimitInfo {
  limit: number;
  used: number;
  remaining: number;
  resetsAt: string;
}

/**
 * Get all photos grouped by country code
 */
export async function getPhotosGrouped(): Promise<Record<string, Photo[]>> {
  const result = await api.get<{ photos: Record<string, Photo[]> }>(
    '/api/photos?grouped=true'
  );
  return result.photos;
}

/**
 * Get photo counts per country
 */
export async function getPhotoCounts(): Promise<Record<string, number>> {
  const result = await api.get<{ counts: Record<string, number> }>(
    '/api/photos?counts=true'
  );
  return result.counts;
}

/**
 * Get photos for a specific country
 */
export async function getCountryPhotos(countryCode: string): Promise<Photo[]> {
  const result = await api.get<{ photos: Photo[] }>(`/api/photos/${countryCode}`);
  return result.photos;
}

/**
 * Replace all photos for a country (bulk update)
 */
export async function replaceCountryPhotos(
  countryCode: string,
  photos: Array<{
    photo_url: string;
    unsplash_id?: string;
    photographer?: string;
    photographer_url?: string;
  }>
): Promise<Photo[]> {
  const result = await api.put<{ photos: Photo[] }>(
    `/api/photos/${countryCode}`,
    { photos }
  );
  return result.photos;
}

/**
 * Add a single photo
 */
export async function addPhoto(data: {
  country_code: string;
  photo_url: string;
  unsplash_id?: string;
  photographer?: string;
  photographer_url?: string;
}): Promise<Photo> {
  const result = await api.post<{ photo: Photo }>('/api/photos', data);
  return result.photo;
}

/**
 * Delete a photo by ID
 */
export async function deletePhoto(id: number): Promise<void> {
  await api.delete(`/api/photos/${id}`);
}

/**
 * Get seeding status
 */
export async function getSeedingStatus(totalCountries: number = 202): Promise<SeedingStatus> {
  return api.get<SeedingStatus>(`/api/seeding/status?total=${totalCountries}`);
}

/**
 * Start seeding process
 */
export async function startSeeding(
  countries: Array<{ code: string; name: string; capital: string }>,
  photosPerCountry: number = 2
): Promise<SeedingResult> {
  return api.post<SeedingResult>('/api/seeding/start', { countries, photosPerCountry });
}

/**
 * Reset seeding progress
 */
export async function resetSeeding(countryCodes?: string[]): Promise<void> {
  await api.post('/api/seeding/reset', { countryCodes });
}

/**
 * Get rate limit info
 */
export async function getRateLimit(): Promise<RateLimitInfo> {
  return api.get<RateLimitInfo>('/api/rate-limit');
}
